import { Box } from '@jfs/ui-new/src/comps/BoxNew'
import { SpacerNew } from '@jfs/ui-new/src/comps/SpacerNew'
import styles from '@jfs/ui-new/src/comps/TextNew/styles'
import { ColorOptions, FontSizeOptions, FontWeightOptions, SpaceOptions } from '@jfs/ui-new/src/theme/UiThemeProvider'
import { useBreakPointProps } from '@jfs/ui-new/src/utils/utils'
import * as React from 'react'
import { FC, useEffect, useRef, useState } from 'react'
import {
  Animated,
  Dimensions,
  Easing,
  KeyboardTypeOptions,
  NativeSyntheticEvent,
  Platform,
  TextInput as RNTextInput,
  ReturnKeyTypeOptions,
  TextInputKeyPressEventData,
  TextInputSubmitEditingEventData,
  TextProps,
} from 'react-native'
import styled from 'styled-components/native'
import { FlexboxProps, color, flexbox, layout, space, typography } from 'styled-system'
import { IconButton } from '../IconButton'
import { IconOptions } from '../IconNew'
import { Spacer } from '../Spacer'
import { Text } from '../TextNew'
import { styles as materialStyles } from './styles'

const StyledTextInput = styled.TextInput<FlexboxProps>`
  ${color}
  ${layout}
  ${typography}
  ${flexbox}
  ${space}
`

type FontWeightValues = 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900'
export interface TextInputProps {
  type?: TypesOption
  innerRef?: React.RefObject<RNTextInput>
  fontSize?: FontSizeOptions | number | Array<FontSizeOptions | number>
  fontWeight?: FontWeightOptions | FontWeightValues | Array<FontWeightOptions | FontWeightValues>
  color?: ColorOptions | Array<ColorOptions>
  selectionColor?: ColorOptions | Array<ColorOptions>
  label?: string
  value?: string
  tintColor?: ColorOptions
  onChange?: (text: string) => void
  onChangeText?: (text: string) => void
  placeholder?: string
  placeholderTextColor?: ColorOptions | Array<ColorOptions>
  showBottomLine?: boolean
  bottomLineColor?: ColorOptions | Array<ColorOptions>
  onFocusBottomLineColor?: ColorOptions | Array<ColorOptions>
  isSecret?: boolean
  maxLength?: number | Array<number>
  leftComponent?: React.ReactNode
  rightComponent?: React.ReactNode
  rightIcon?: IconOptions
  rightIconColor?: ColorOptions | Array<ColorOptions>
  rightAction?: string
  rightOnPress?: () => void
  keyboardType?: KeyboardTypeOptions
  editable?: boolean
  multiline?: boolean
  numberOfLines?: number
  onBlur?: () => void
  onFocus?: () => void
  onKeyPress?: (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => void
  secureTextEntry?: boolean
  autoCompleteType?:
    | 'cc-csc'
    | 'cc-exp'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-number'
    | 'email'
    | 'name'
    | 'password'
    | 'postal-code'
    | 'street-address'
    | 'tel'
    | 'username'
    | 'off'
  autoComplete?:
    | 'cc-csc'
    | 'cc-name'
    | 'cc-exp'
    | 'cc-exp-month'
    | 'cc-exp-year'
    | 'cc-number'
    | 'email'
    | 'name'
    | 'password'
    | 'postal-code'
    | 'street-address'
    | 'tel'
    | 'username'
    | 'off'
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters'
  autoCorrect?: boolean
  onPressIn?: () => void
  onPressOut?: () => void
  error?: string
  touched?: boolean
  autoFocus?: boolean
  weight?: 'normal' | 'dense' | Array<'normal' | 'dense'>
  textAlign?: 'auto' | 'left' | 'right' | 'center' | 'justify' | Array<'auto' | 'left' | 'right' | 'center' | 'justify'>
  textWidth?: number | Array<number>
  hide?: boolean
  returnKeyType?: ReturnKeyTypeOptions
  onSubmitEditing?: (e: NativeSyntheticEvent<TextInputSubmitEditingEventData>) => void
  testID?: string
  showOwnError?: boolean
  errorColor?: ColorOptions
  fontFamily?: string
  onSelectionChange?: (event: any) => void
  isStartSelection?: Boolean
  cursorPosition?: number
  errorMargin?: SpaceOptions | number
  animatedColorValue?: string
}

type TypesOption = keyof typeof styles
export const TextTypesOptionArray = Object.keys(styles)

const removeOutline =
  Platform.OS === 'web'
    ? {
        outline: 'none',
      }
    : {}

const defaultBottomValue = 5
const animatedBottomValue = 30

const animatedLeftValue = 0

const defaultLargeFontSizeValue = 16
const animatedLargeFontSizeValue = 12

const defaultFontSizeValue = 14
const animatedFontSizeValue = 12

export const MaterialTextInput: FC<TextInputProps> = (props) => {
  const modifiedProps = useBreakPointProps<TextInputProps>(props)
  const [isFocused, setFocused] = useState(false)
  const [leftComponentWidth, setLeftComponentWidth] = useState(0)
  const [isPressed, setPressed] = useState(false)
  const [backSpacePress, setBackSpacePress] = useState(false)
  const isLargeScreen = React.useMemo(() => Dimensions.get('window').width > 550, [])
  const isWeb = Platform.OS === 'web'
  const shouldDelay = isWeb && !isLargeScreen

  const {
    type = 'body14',
    weight = 'normal',
    label,
    onChange: onChangeText,
    placeholder,
    rightComponent = null,
    rightIcon,
    rightIconColor = 'TEXT_PRIMARY',
    rightAction,
    rightOnPress,
    onFocus,
    onBlur,
    tintColor,
    value,
    innerRef,
    error,
    touched,
    showOwnError = false,
    editable = true,
    placeholderTextColor,
    errorColor,
    leftComponent = null,
    onSelectionChange,
    isStartSelection = false,
    cursorPosition,
    errorMargin = 'TINY',
    animatedColorValue = '#2369FF',
    ...restProps
  } = modifiedProps

  const textInputRef = useRef<RNTextInput>(null)
  const defaultColorValue = (placeholderTextColor as ColorOptions) || '#7F7F7F'
  const defaultLeftValue = leftComponent ? leftComponentWidth : 0

  const animatedValue = useRef(new Animated.Value(value ? 1 : 0))
  const labelRef = useRef(null)
  // TintColor
  const isError =
    (touched === undefined || touched) &&
    value &&
    (value.length === props.maxLength || !isFocused) &&
    !!error &&
    !isPressed &&
    value &&
    value.length > 0

  const tintColorProp: ColorOptions = isError
    ? errorColor || 'ERROR'
    : isFocused
    ? tintColor || 'BRAND_PRIMARY_ACTION'
    : editable
    ? 'BRAND_COLOR_GRAY_3'
    : tintColor || 'BRAND_PRIMARY_ACTION'

  const typeStyle = type ? styles[type][weight as 'normal' | 'dense'] : {}

  let right = null

  if (rightComponent) {
    right = rightComponent
  } else if (rightIcon) {
    if (!rightOnPress) {
      throw Error('rightOnPress is required')
    }
    right = <IconButton onPress={rightOnPress} color={rightIconColor as ColorOptions} id={rightIcon as IconOptions} />
  } else if (rightAction) {
    if (!rightOnPress) {
      throw Error('rightOnPress is required')
    }
    right = (
      <Box underlayColor={'transparent' as ColorOptions} onPress={rightOnPress}>
        <Text type={'tertiaryAction12'}>{rightAction}</Text>
      </Box>
    )
  }

  /*
  autoFocus prop not triggering the animation so had to add useEffect,
  as componentDidMount
  */

  useEffect(() => {
    if (restProps.autoFocus) {
      onInternalFocus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refUsed = innerRef ?? textInputRef

  const onInternalFocus = () => {
    if (!value) {
      setTimeout(
        () => {
          Animated.timing(animatedValue.current, {
            toValue: 1,
            duration: 200,
            easing: Easing.linear,
            useNativeDriver: false,
          }).start(() => setFocused(true))
        },
        shouldDelay ? 400 : 0,
      )
    } else {
      setFocused(true)
    }
    if (onFocus) {
      onFocus()
    }
  }

  const onInternalBlur = () => {
    setBackSpacePress(false)
    if (!value) {
      setTimeout(
        () => {
          Animated.timing(animatedValue.current, {
            toValue: 0,
            duration: 200,
            easing: Easing.linear,
            useNativeDriver: false,
          }).start(() => setFocused(false))
        },
        shouldDelay ? 400 : 0,
      )
    } else {
      setFocused(false)
    }
    if (onBlur) {
      onBlur()
    }
  }

  // To animate the text in upward direction
  const bottomValue = animatedValue.current.interpolate({
    inputRange: [0, 1],
    outputRange: [defaultBottomValue, animatedBottomValue],
  })

  const leftValue = animatedValue.current.interpolate({
    inputRange: [0, 1],
    outputRange: [defaultLeftValue, animatedLeftValue],
  })

  // To animate the font size
  const largeFontSizeValue = animatedValue.current.interpolate({
    inputRange: [0, 1],
    outputRange: [defaultLargeFontSizeValue, animatedLargeFontSizeValue],
  })

  const fontSizeValue = animatedValue.current.interpolate({
    inputRange: [0, 1],
    outputRange: [defaultFontSizeValue, animatedFontSizeValue],
  })

  // To animate the text color
  const colorValue = animatedValue.current.interpolate({
    inputRange: [0, 1],
    outputRange: [
      defaultColorValue,
      props?.bottomLineColor ? (props?.bottomLineColor as ColorOptions) : animatedColorValue,
    ],
  })

  const getAnimatedTextColor = () => {
    if ((value && !isFocused) || !editable) {
      return defaultColorValue
    }
    return colorValue
  }

  // Set all the animated values
  const textLabelStyle = {
    bottom: value ? animatedBottomValue : bottomValue,
    left: value ? animatedLeftValue : leftValue,
    color: getAnimatedTextColor(),
    fontWeight: '500',
    position: 'absolute',
    fontSize: isLargeScreen
      ? value
        ? animatedLargeFontSizeValue
        : largeFontSizeValue
      : value
      ? animatedFontSizeValue
      : fontSizeValue,
    fontFamily: 'JioType-Medium',
  } as TextProps

  const onInternalKeyPress = ({ nativeEvent }: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    if (nativeEvent?.key === 'Backspace' && !backSpacePress) {
      setBackSpacePress(true)
    }
  }

  /**
   * In case of any validation error
   * We are rendering a Spacer in when there is no error so that TextInput won't change it's position
   */
  const showErrorView = () => {
    if (showOwnError) {
      return null
    }
    if (error && isError) {
      return (
        <Box height={'16px'} direction={'row'} align={'center'}>
          <Text type={'label'} color={(errorColor as ColorOptions) || 'ERROR'}>
            {error}
          </Text>
        </Box>
      )
    } else {
      return <Spacer direction={'vertical'} size={'16px' as SpaceOptions} />
    }
  }

  return (
    <Box width="100%">
      <Box height={'50px'} justify={'flex-end'}>
        <Box direction={'row'} align={'center'} height={'25px'} {...materialStyles.underline(tintColorProp)}>
          <Box
            onLayout={(event) => {
              const { width } = event.nativeEvent.layout
              setLeftComponentWidth(width + 8)
            }}>
            {leftComponent}
          </Box>
          {leftComponent && <Spacer direction="horizontal" size={8} />}
          <Box
            f={1}
            onPressIn={() => {
              setPressed(true)
            }}
            onPressOut={() => {
              setPressed(false)
            }}>
            <StyledTextInput
              padding={0}
              onChangeText={onChangeText}
              onBlur={onInternalBlur}
              onFocus={onInternalFocus}
              value={value}
              onKeyPress={onInternalKeyPress}
              ref={refUsed}
              fontSize={16}
              fontWeight="700"
              editable={editable}
              onSelectionChange={onSelectionChange}
              selection={
                isStartSelection
                  ? { start: 0, end: 0 }
                  : cursorPosition !== -1 && cursorPosition !== undefined
                  ? { start: cursorPosition, end: cursorPosition }
                  : null
              }
              {...typeStyle}
              {...restProps}
              underlineColorAndroid={'transparent'}
              minHeight={45}
              //backgroundColor={'red'}
              placeholder={''}
              style={removeOutline}
            />
          </Box>
          {right}
        </Box>
        <Animated.Text onPress={() => refUsed?.current?.focus()} ref={labelRef} style={textLabelStyle}>
          {label || placeholder}
        </Animated.Text>
      </Box>
      <SpacerNew size={errorMargin} direction={'vertical'} />
      {showErrorView()}
    </Box>
  )
}
